import { Granular, Reverb, Saturation, safetyCurve, type Effect } from './effects';
import type { Instrument } from './instrument';
import type { Preset } from './presets';
import { loadPianoBank, Sampler, type PianoBank } from './sampler';
import { FmSynth, Synth } from './synth';

export type EffectId = 'reverb' | 'saturation' | 'granular';

export interface EffectSettings {
  mix: number;
  a: number;
  b: number;
}

/** Each effect's two character knobs, in order. */
export const EFFECT_PARAMS: Record<EffectId, [string, string]> = {
  reverb: ['size', 'tone'],
  saturation: ['drive', 'tone'],
  granular: ['size', 'pitch'],
};

/**
 * Signal flow:
 *   instrument -> saturation -> granular -> reverb -> volume -> limiter -> soft clip -> output
 *   output -> speakers, analyser (visuals), recorder (lossless capture)
 */
export class AudioEngine {
  readonly ctx: AudioContext;
  readonly output: GainNode;
  readonly analyser: AnalyserNode;
  recorderNode: AudioWorkletNode | null = null;
  private instrumentBus: GainNode;
  private volume: GainNode;
  private effects: Partial<Record<EffectId, Effect>> = {};
  private instrument: Instrument | null = null;
  private preset: Preset | null = null;
  private bank: PianoBank | null = null;
  private pendingFx: Partial<Record<EffectId, EffectSettings>> = {};
  private appliedFx: Partial<Record<EffectId, EffectSettings>> = {};
  private streamDest: MediaStreamAudioDestinationNode | null = null;
  // Controller state outlives instruments, so switching sound mid-performance
  // keeps the wheels and pedal where they are.
  private bend = 0;
  private mod = 0;
  private sustain = false;
  readonly ready: Promise<void>;

  constructor() {
    this.ctx = new AudioContext({ latencyHint: 'interactive' });
    this.instrumentBus = this.ctx.createGain();
    this.volume = this.ctx.createGain();
    this.volume.gain.value = 0.8;
    const limiter = this.ctx.createDynamicsCompressor();
    limiter.threshold.value = -4;
    limiter.knee.value = 4;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.2;
    this.output = this.ctx.createGain();
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.78;
    const safety = this.ctx.createWaveShaper();
    safety.curve = safetyCurve();
    safety.oversample = '2x';
    this.volume.connect(limiter).connect(safety).connect(this.output);
    this.output.connect(this.ctx.destination);
    this.output.connect(this.analyser);
    // Until the worklet-based effects are ready, play straight through.
    this.instrumentBus.connect(this.volume);
    this.ready = this.init();
  }

  private async init(): Promise<void> {
    // Served as plain files from /public: some browsers refuse data: URL worklets.
    const base = import.meta.env.BASE_URL;
    await Promise.all([
      this.ctx.audioWorklet.addModule(`${base}worklets/granular-processor.js`),
      this.ctx.audioWorklet.addModule(`${base}worklets/recorder-processor.js`),
    ]);
    const saturation = new Saturation(this.ctx);
    const granular = new Granular(this.ctx);
    const reverb = new Reverb(this.ctx);
    this.effects = { saturation, granular, reverb };
    saturation.output.connect(granular.input);
    granular.output.connect(reverb.input);
    reverb.output.connect(this.volume);
    this.instrumentBus.disconnect();
    this.instrumentBus.connect(saturation.input);
    for (const [id, s] of Object.entries(this.pendingFx)) this.setEffect(id as EffectId, s);

    this.recorderNode = new AudioWorkletNode(this.ctx, 'spiel-recorder', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
    // Route through a muted gain so the browser keeps pulling the node.
    const silent = this.ctx.createGain();
    silent.gain.value = 0;
    this.output.connect(this.recorderNode).connect(silent).connect(this.ctx.destination);
  }

  /** Browsers start audio suspended until a user gesture. */
  resume(): void {
    if (this.ctx.state !== 'running') void this.ctx.resume();
  }

  get running(): boolean {
    return this.ctx.state === 'running';
  }

  loadPiano(onProgress?: (f: number) => void): Promise<PianoBank> {
    return loadPianoBank(this.ctx, onProgress).then((bank) => {
      this.bank = bank;
      if (this.preset?.kind === 'sampler' && !this.instrument) this.setPreset(this.preset);
      return bank;
    });
  }

  /** Swap the instrument. Returns false if it needs samples that aren't loaded yet. */
  setPreset(preset: Preset): boolean {
    this.preset = preset;
    this.instrument?.dispose();
    this.instrument = null;
    if (preset.kind === 'sampler') {
      if (!this.bank) return false;
      this.instrument = new Sampler(this.ctx, preset, this.bank, this.instrumentBus);
    } else if (preset.kind === 'fm') {
      this.instrument = new FmSynth(this.ctx, preset, this.instrumentBus);
    } else {
      this.instrument = new Synth(this.ctx, preset, this.instrumentBus);
    }
    const t = this.ctx.currentTime;
    if (this.bend) this.instrument.setBend(this.bend * 100, t);
    if (this.mod) this.instrument.setMod(this.mod, t);
    if (this.sustain) this.instrument.setSustain(true, t);
    return true;
  }

  get hasInstrument(): boolean {
    return this.instrument !== null;
  }

  noteOn(note: number, velocity: number): void {
    this.instrument?.noteOn(note, velocity, this.ctx.currentTime);
  }

  noteOff(note: number): void {
    this.instrument?.noteOff(note, this.ctx.currentTime);
  }

  allNotesOff(): void {
    this.instrument?.allNotesOff(this.ctx.currentTime);
  }

  setSustain(on: boolean): void {
    this.sustain = on;
    this.instrument?.setSustain(on, this.ctx.currentTime);
  }

  setBend(semitones: number): void {
    this.bend = semitones;
    this.instrument?.setBend(semitones * 100, this.ctx.currentTime);
  }

  setMod(amount: number): void {
    this.mod = amount;
    this.instrument?.setMod(amount, this.ctx.currentTime);
  }

  setVolume(v: number): void {
    // Squared so the knob feels even across its range.
    this.volume.gain.setTargetAtTime(v * v * 1.25, this.ctx.currentTime, 0.02);
  }

  setEffect(id: EffectId, s: EffectSettings): void {
    const fx = this.effects[id];
    if (!fx) {
      this.pendingFx[id] = s;
      return;
    }
    const [a, b] = EFFECT_PARAMS[id];
    const prev = this.appliedFx[id];
    // Only touch what changed: some params (reverb size) are expensive.
    if (prev?.mix !== s.mix) fx.setMix(s.mix);
    if (prev?.a !== s.a) fx.setParam(a, s.a);
    if (prev?.b !== s.b) fx.setParam(b, s.b);
    this.appliedFx[id] = { ...s };
  }

  /** A MediaStream of the master output, for recording video with sound. */
  mediaStream(): MediaStream {
    if (!this.streamDest) {
      this.streamDest = this.ctx.createMediaStreamDestination();
      this.output.connect(this.streamDest);
    }
    return this.streamDest.stream;
  }
}
